/**
 * Licence v aplikaci: zkušební sezóna, upozornění na úvodní stránce, zadání
 * licenčního kódu, připomínka konce platnosti do kalendáře a (pro autora)
 * vydávání licenčních kódů.
 *
 * Po skončení platnosti zůstanou data k prohlížení, tisku, exportu i záloze
 * (evidence léčiv musí zůstat dostupná) – zablokují se jen nové zápisy a úpravy.
 */
import { useEffect, useState } from 'react';
import { getSetting, LOCKED_MESSAGE, read, setSetting, setWriteLock } from '../db/db';
import { addDays, todayISO } from '../db/rules';
import { googleCalendarUrl, icsCalendar, type IcsEvent } from '../lib/ics';
import {
  canWrite,
  licenceStatus,
  privateKeyMatches,
  signLicence,
  verifyLicence,
  validUntil,
  type LicencePayload,
  type LicenceStatus,
  type StoredLicence,
} from '../lib/licence';
import { go, useQuery } from './hooks';
import { fmtDate, plural } from './labels';
import { Chips, DateInput, Field, Link, Notice, Screen, Section, TextInput, toast } from './ui';
import { Icon } from './icons';
import { saved } from './confirm';
import { downloadIcs } from './report-reminder';

const APP_URL = 'https://vcelarska-aplikace.pages.dev/';

export interface LicenceState {
  status: LicenceStatus;
  trialStart: string;
  licence?: StoredLicence;
}

/**
 * Zjistí stav licence a podle něj zamkne/odemkne zápisy.
 * Začátek zkušební doby = nejstarší datum v datech aplikace (výchozí přípravky
 * vznikají při první instalaci), takže ho nelze posunout obnovou zálohy.
 * Den „dnes“ nikdy nejde zpět – přetočení hodin v telefonu nepomůže.
 */
let serverDay: string | null | undefined;
/** Dnešní datum podle serveru (hlavička Date), nebo null bez internetu. Zjišťuje se jednou za spuštění. */
async function serverDate(): Promise<string | null> {
  if (serverDay !== undefined) return serverDay;
  if (!navigator.onLine) return null;
  try {
    const r = await fetch('./manifest.webmanifest', { method: 'HEAD', cache: 'no-store' });
    const d = r.headers.get('Date');
    serverDay = d ? todayISO(new Date(d)) : null;
  } catch {
    serverDay = null;
  }
  return serverDay;
}

export async function loadLicence(): Promise<LicenceState> {
  const now = todayISO();
  const [storedStart, lastSeen, licence, products, apiaries, colonies] = await Promise.all([
    getSetting<string>('trialStart'),
    getSetting<string>('lastSeen'),
    getSetting<StoredLicence>('licence'),
    read.all('products'),
    read.all('apiaries'),
    read.all('colonies'),
  ]);
  let trialStart = storedStart ?? now;
  for (const x of [...products, ...apiaries, ...colonies]) {
    const d = x.createdAt?.slice(0, 10);
    if (d && d < trialStart) trialStart = d;
  }
  let today = lastSeen && lastSeen > now ? lastSeen : now;
  // „Poslední viděné datum“ je o víc než den napřed: buď měl telefon dřív špatně
  // nastavené hodiny, nebo je někdo vrací zpět. S internetem rozhodne čas serveru.
  if (lastSeen && lastSeen > addDays(now, 1)) {
    const server = await serverDate();
    if (server) today = server;
  }
  if (trialStart !== storedStart) await setSetting('trialStart', trialStart);
  if (today !== lastSeen) await setSetting('lastSeen', today);
  const status = licenceStatus(trialStart, today, licence?.payload);
  setWriteLock(!canWrite(status));
  return { status, trialStart, licence };
}

export const useLicence = () => useQuery(loadLicence, []);

/** Zápis zablokovaný licencí (např. smazání, rychlé tlačítko) → srozumitelná hláška místo chyby. */
export function installLicenceGuard() {
  addEventListener('unhandledrejection', (e) => {
    if (e.reason instanceof Error && e.reason.message === LOCKED_MESSAGE) {
      e.preventDefault();
      e.stopImmediatePropagation();
      toast(LOCKED_MESSAGE);
    }
  });
  void loadLicence().catch(() => {});
}

/** Obrazovky, které něco zapisují – po skončení platnosti se nahradí upozorněním. */
export function isWriteRoute([a, b, c]: string[]) {
  if (a === 'zapis' || a === 'pripravek') return true;
  if (a === 'stanoviste') return b === 'nove' || c === 'upravit';
  if (a === 'vcelstvo' || a === 'v') return b === 'nove' || c === 'upravit' || c === 'matka' || c === 'oddelek';
  return false;
}

// ---------------------------------------------------------------------------
// Upozornění na úvodní stránce
// ---------------------------------------------------------------------------

export function LicenceNotice() {
  const lic = useLicence();
  const intro = useQuery(async () => !(await getSetting<boolean>('trialIntroSeen')), []);
  if (!lic) return null;
  const s = lic.status;
  if (s.kind === 'expired')
    return (
      <Notice tone="warn">
        <strong>{s.annualExpired ? 'Platnost licence skončila.' : 'Zkušební sezóna skončila.'}</strong> Data si dál můžete prohlížet,
        tisknout a zálohovat, nové zápisy jsou ale zablokované. <Link to="licence">Aktivovat licenci</Link>
      </Notice>
    );
  // Měsíc před koncem: zápisy budou za X dní zablokované.
  if ((s.kind === 'trial' || s.kind === 'annual') && s.daysLeft <= 30)
    return (
      <Notice tone="warn">
        <strong>{s.kind === 'trial' ? 'Zkušební sezóna končí' : 'Roční licence končí'} {fmtDate(s.until)}</strong> (za{' '}
        {plural(s.daysLeft, 'den', 'dny', 'dní')}). Potom budou nové zápisy možné až po {s.kind === 'trial' ? 'zakoupení' : 'prodloužení'}{' '}
        licence, data vám zůstanou. <Link to="licence">{s.kind === 'trial' ? 'Pořídit licenci' : 'Prodloužit'}</Link>
      </Notice>
    );
  // Jednou po instalaci: krátké upozornění na zkušební verzi a zálohy.
  if (s.kind === 'trial' && intro)
    return (
      <Notice tone="info">
        <strong>Úlník je ve zkušební verzi.</strong> Poskytuje se zdarma, bez záruky. Data jsou jen ve vašem telefonu –{' '}
        <strong>pravidelně zálohujte</strong> (Nastavení → Záloha dat).
        <div className="quick-row" style={{ marginTop: 10 }}>
          <button className="btn small" onClick={() => void setSetting('trialIntroSeen', true)}>
            Rozumím
          </button>
        </div>
      </Notice>
    );
  return null;
}

// ---------------------------------------------------------------------------
// Popis stavu (Nastavení i obrazovka Licence)
// ---------------------------------------------------------------------------

function StatusText({ s }: { s: LicenceStatus }) {
  switch (s.kind) {
    case 'full':
      return (
        <p>
          <Icon name="check" size={18} /> <strong>Trvalá licence</strong> – {s.name}. Děkujeme!
        </p>
      );
    case 'annual':
      return (
        <p>
          <strong>Roční licence</strong> – {s.name}, platí do <strong>{fmtDate(s.until)}</strong> (zbývá{' '}
          {plural(s.daysLeft, 'den', 'dny', 'dní')}).
        </p>
      );
    case 'trial':
      return (
        <p>
          <strong>Zkušební sezóna</strong> – zdarma do <strong>{fmtDate(s.until)}</strong> (zbývá {plural(s.daysLeft, 'den', 'dny', 'dní')}).
          {s.annualExpired && <> Roční licence skončila {fmtDate(s.annualExpired)}.</>}
        </p>
      );
    case 'expired':
      return (
        <p>
          <strong>{s.annualExpired ? 'Licence vypršela' : 'Zkušební sezóna skončila'}</strong> – od {fmtDate(s.since)} lze data jen prohlížet,
          tisknout a zálohovat.
        </p>
      );
  }
}

export function LicenceSetting() {
  const lic = useLicence();
  if (!lic) return null;
  return (
    <Section title="Licence">
      <StatusText s={lic.status} />
      <button className="btn wide" onClick={() => go('licence')}>
        {lic.status.kind === 'full' ? 'Podrobnosti o licenci' : 'Zadat licenční kód / připomínka do kalendáře'}
      </button>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Připomínka konce platnosti do kalendáře
// ---------------------------------------------------------------------------

function endEvent(s: LicenceStatus, until: string, alarmDays: number): IcsEvent {
  const trial = s.kind === 'trial';
  return {
    uid: `konec-${trial ? 'zkusebni-sezony' : 'licence'}-${until}@vcelarska-aplikace.pages.dev`,
    date: until,
    time: '09:00',
    durationMin: 15,
    title: trial ? 'Úlník – končí zkušební sezóna' : 'Úlník – končí roční licence',
    description:
      `Aplikace Úlník funguje naplno do ${fmtDate(until)}. Potom půjdou data jen prohlížet a zálohovat.\n` +
      'Licenci aktivujete v aplikaci: Nastavení → Licence.',
    url: APP_URL + '#/licence',
    alarmDaysBefore: alarmDays,
  };
}

function CalendarReminder({ s }: { s: LicenceStatus }) {
  const [alarm, setAlarm] = useState(14);
  const until = validUntil(s);
  if (!until) return null;
  const ev = endEvent(s, until, alarm);
  return (
    <Section title="Připomínka do kalendáře">
      <p className="muted">Kalendář v telefonu vám včas připomene, že platnost končí {fmtDate(until)}.</p>
      <Field label="Upozornit">
        <Chips
          ariaLabel="Upozornit"
          options={[
            [7, 'týden předem'],
            [14, '2 týdny předem'],
            [30, 'měsíc předem'],
          ]}
          value={alarm}
          onChange={(v) => v != null && setAlarm(v)}
        />
      </Field>
      <div className="quick-row">
        <button
          className="btn primary"
          onClick={() => {
            downloadIcs(icsCalendar(ev), 'ulnik-konec-platnosti.ics');
            saved('Připomínka připravena');
            setTimeout(() => toast('Otevřete stažený soubor a potvrďte Přidat do kalendáře'), 1400);
          }}
        >
          <Icon name="calendar" size={20} /> Přidat do kalendáře
        </button>
        <a className="btn" href={googleCalendarUrl(ev)} target="_blank" rel="noopener noreferrer">
          Google Kalendář
        </a>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Obrazovka Licence: stav, zadání kódu, připomínka
// ---------------------------------------------------------------------------

export function LicenceScreen() {
  const lic = useLicence();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  if (!lic) return <Screen title="Licence">{null}</Screen>;
  const s = lic.status;

  const activate = async () => {
    setBusy(true);
    const p = await verifyLicence(code);
    setBusy(false);
    if (!p) return toast('Kód není platný. Zkontrolujte, že jste ho zkopírovali celý.');
    if (p.k === 'R' && p.e! < todayISO()) return toast(`Tato roční licence už skončila (${fmtDate(p.e!)}).`);
    await setSetting<StoredLicence>('licence', { code: code.replace(/\s+/g, ''), payload: p, activatedAt: new Date().toISOString() });
    setCode('');
    saved(p.k === 'T' ? 'Licence aktivována' : `Licence platí do ${fmtDate(p.e!)}`);
  };

  return (
    <Screen title="Licence" crumbs={[['Nastavení', 'nastaveni']]} backTo="nastaveni">
      <Section>
        <StatusText s={s} />
        {s.kind === 'trial' && (
          <p className="muted">
            Úlník si můžete se všemi funkcemi zdarma vyzkoušet vždy až do 31. 3. – minimálně po celou jednu včelařskou sezónu. Když vám
            bude vyhovovat, pořiďte si licenci – jednorázově natrvalo, nebo jako roční předplatné. Vaše data zůstanou, jak jsou.
          </p>
        )}
        {lic.licence && <p className="small muted">Licenční kód aktivován {fmtDate(lic.licence.activatedAt)}.</p>}
        <p className="small disclaimer-box">
          Úlník je zatím ve zkušební verzi a poskytuje se zdarma, bez záruky. Data jsou jen ve vašem telefonu – pravidelně zálohujte. <Link to="nastaveni">Záloha v Nastavení</Link>
        </p>
      </Section>

      {s.kind !== 'full' && (
        <Section title="Mám licenční kód">
          <Field label="Licenční kód" htmlFor="lic-code" hint="Začíná ULNIK-. Vložte ho celý, i když je dlouhý.">
            <textarea
              id="lic-code"
              rows={4}
              value={code}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              placeholder="ULNIK-…"
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          <button className="btn primary wide" disabled={busy || code.trim().length < 20} onClick={activate}>
            Aktivovat
          </button>
        </Section>
      )}

      {s.kind !== 'full' && (
        <Section title="Jak licenci získat">
          <p className="muted">Napište autorovi aplikace – zpráva přijde přímo do aplikace. Licenční kód vám pošle zpět.</p>
          <button className="btn wide" onClick={() => go('pripominky')}>
            <Icon name="mail" size={20} /> Napsat autorovi
          </button>
        </Section>
      )}

      <CalendarReminder s={s} />
      <p className="small muted center">
        <button type="button" className="btn link small-link" onClick={() => go('licence/vydat')}>
          pro autora aplikace: vydat licenci
        </button>
      </p>
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Místo formuláře po skončení platnosti
// ---------------------------------------------------------------------------

export function LockedScreen() {
  return (
    <Screen title="Zápis není možný">
      <Notice tone="warn">
        <strong>Platnost aplikace skončila.</strong> Vaše data zůstávají – můžete je prohlížet, tisknout, exportovat i zálohovat. Pro nové
        zápisy a úpravy aktivujte licenci.
      </Notice>
      <button className="btn primary big wide" onClick={() => go('licence')}>
        Aktivovat licenci
      </button>
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Pro autora: vydání licenčního kódu (#/licence/vydat)
// Soukromý klíč zůstává jen v tomto telefonu, do zálohy se neukládá.
// ---------------------------------------------------------------------------

export function LicenceIssueScreen() {
  const [key, setKey] = useState<JsonWebKey | null | undefined>(undefined);
  const [keyText, setKeyText] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'T' | 'R'>('T');
  const [until, setUntil] = useState(addDays(todayISO(), 365 - 1));
  const [code, setCode] = useState('');

  useEffect(() => {
    getSetting<JsonWebKey>('licencePrivateKey').then((k) => setKey(k ?? null));
  }, []);
  if (key === undefined) return null;

  const saveKey = async () => {
    let jwk: JsonWebKey;
    try {
      jwk = JSON.parse(keyText.trim());
    } catch {
      return toast('Klíč se nepodařilo přečíst – vložte celý obsah souboru.');
    }
    if (!(await privateKeyMatches(jwk))) return toast('Tento klíč nepatří k aplikaci Úlník.');
    await setSetting('licencePrivateKey', jwk);
    setKey(jwk);
    setKeyText('');
    saved('Klíč uložen');
  };

  const issue = async () => {
    if (!key || !name.trim()) return;
    const p: LicencePayload = { v: 1, n: name.trim(), k: kind, d: todayISO(), ...(kind === 'R' ? { e: until } : {}) };
    setCode(await signLicence(p, key));
    saved('Kód vytvořen');
  };

  const message = code
    ? `Dobrý den, děkuji za zakoupení aplikace Úlník.\n\nVáš licenční kód:\n${code}\n\n` +
      'Zkopírujte celý kód, v aplikaci otevřete Nastavení → Licence → Mám licenční kód, vložte ho a ťukněte na Aktivovat.'
    : '';

  return (
    <Screen title="Vydat licenci" crumbs={[['Nastavení', 'nastaveni']]} backTo="nastaveni">
      {!key ? (
        <Section title="Klíč autora">
          <p className="muted">
            Licenční kódy může vytvářet jen ten, kdo má soukromý klíč autora. Vložte obsah souboru s klíčem – uloží se jen v tomto telefonu.
          </p>
          <textarea rows={5} value={keyText} spellCheck={false} onChange={(e) => setKeyText(e.target.value)} placeholder='{"kty":"EC",…}' />
          <button className="btn primary wide" disabled={!keyText.trim()} onClick={saveKey}>
            Uložit klíč
          </button>
        </Section>
      ) : (
        <>
          <Section title="Nová licence">
            <Field label="Jméno zákazníka" htmlFor="li-name">
              <TextInput id="li-name" value={name} onChange={setName} />
            </Field>
            <Field label="Druh">
              <Chips
                ariaLabel="Druh licence"
                options={[
                  ['T', 'Trvalá (jednorázově)'],
                  ['R', 'Roční (předplatné)'],
                ]}
                value={kind}
                onChange={(v) => v && setKind(v)}
              />
            </Field>
            {kind === 'R' && (
              <Field label="Platí do (včetně)" htmlFor="li-until">
                <DateInput id="li-until" value={until} min={todayISO()} onChange={setUntil} />
              </Field>
            )}
            <button className="btn primary wide" disabled={!name.trim()} onClick={issue}>
              Vytvořit licenční kód
            </button>
          </Section>
          {code && (
            <Section title="Kód pro zákazníka">
              <textarea rows={5} readOnly value={code} onFocus={(e) => e.target.select()} />
              <div className="quick-row">
                <button
                  className="btn primary"
                  onClick={() => navigator.clipboard?.writeText(message).then(() => toast('Zpráva s kódem zkopírována'), () => toast('Kopírování se nepovedlo – označte kód ručně.'))}
                >
                  Kopírovat zprávu
                </button>
                {'share' in navigator && (
                  <button className="btn" onClick={() => navigator.share({ title: 'Licence Úlník', text: message }).catch(() => {})}>
                    Sdílet (SMS, e-mail…)
                  </button>
                )}
              </div>
            </Section>
          )}
          <Section>
            <button
              className="btn link"
              onClick={async () => {
                await setSetting('licencePrivateKey', null);
                setKey(null);
                toast('Klíč z tohoto telefonu odstraněn');
              }}
            >
              Odstranit klíč z tohoto telefonu
            </button>
          </Section>
        </>
      )}
    </Screen>
  );
}
